import type { AdminSummary, Contact, DebriefView, ObservedView, PlayerAction, RunMode, RunSummary, ScenarioSummary, ScoringExplanation, User } from "./types";

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

/** An API error with per-field messages (sign-up validation). */
export class FieldsError extends ApiError {
  constructor(status: number, message: string, readonly fields: Record<string, string>) {
    super(status, message);
  }
}

async function request<T>(method: "GET" | "POST", path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
  const init: RequestInit = { method, credentials: "same-origin", headers: { accept: "application/json", ...headers } };
  if (body !== undefined) {
    init.headers = { ...init.headers, "content-type": "application/json" };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(`/api${path}`, init);
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = null; }
  }
  if (!res.ok) {
    const msg = data && typeof data === "object" && "error" in data ? String((data as { error: unknown }).error) : res.statusText;
    const fields = data && typeof data === "object" && "fields" in data ? (data as { fields: unknown }).fields : null;
    if (fields && typeof fields === "object") throw new FieldsError(res.status, msg || `Request failed (${res.status})`, fields as Record<string, string>);
    throw new ApiError(res.status, msg || `Request failed (${res.status})`);
  }
  return data as T;
}

export interface RunResponse {
  runId: string; view: ObservedView;
  /** Sent by GET /view (not by POST /actions): whether the scoring page has been revealed for this run. */
  scoringRevealed?: boolean;
}
export interface ScoringResponse { runId: string; scoringRevealed: boolean; scoring: ScoringExplanation }

const run = (runId: string) => `/runs/${encodeURIComponent(runId)}`;
const admin = (token: string) => ({ "x-admin-token": token });

export const api = {
  me: () => request<{ user: User }>("GET", "/me"),
  config: () => request<{ orgName: string }>("GET", "/config"),
  signup: (body: Record<string, string>) => request<{ user: User }>("POST", "/auth/signup", body),
  logout: () => request<{ ok: true }>("POST", "/auth/logout"),
  scenarios: () => request<{ scenarios: ScenarioSummary[] }>("GET", "/scenarios"),
  runs: () => request<{ runs: RunSummary[] }>("GET", "/runs"),
  startRun: (scenarioId: string, mode: RunMode = "full") => request<RunResponse>("POST", "/runs", { scenarioId, mode }),
  abandonRun: (runId: string) => request<{ runId: string; status: string }>("POST", `/runs/${runId}/abandon`, {}),
  view: (runId: string) => request<RunResponse>("GET", `/runs/${encodeURIComponent(runId)}/view`),
  act: (runId: string, action: PlayerAction) => request<RunResponse>("POST", `/runs/${encodeURIComponent(runId)}/actions`, action),
  debrief: (runId: string) => request<{ runId: string; debrief: DebriefView }>("GET", `/runs/${encodeURIComponent(runId)}/debrief`),
  feedback: (runId: string, answers: Record<string, unknown>) => request<{ ok: true }>("POST", `/runs/${encodeURIComponent(runId)}/feedback`, answers),
  /** 403 while the run is active and scoring has not been revealed. */
  scoring: (runId: string) => request<ScoringResponse>("GET", `${run(runId)}/scoring`),
  /** Permanent for the run, and marks it in playtest results. */
  revealScoring: (runId: string) => request<ScoringResponse>("POST", `${run(runId)}/scoring/reveal`),
  /** "Email me my results": "queued" means saved but not (yet) sent. */
  resultsEmail: (runId: string, email: string, contactConsent: boolean) =>
    request<{ status: "sent" | "queued"; email: string }>("POST", `${run(runId)}/results-email`, { email, contactConsent }),
  adminContacts: (token: string) => request<{ contacts: Contact[] }>("GET", "/admin/contacts", undefined, admin(token)),
  /** The contacts as CSV text, to save as a file. */
  adminContactsCsv: async (token: string): Promise<string> => {
    const res = await fetch("/api/admin/contacts.csv", { credentials: "same-origin", headers: admin(token) });
    if (!res.ok) throw new ApiError(res.status, res.status === 403 ? "admin access required" : `Request failed (${res.status})`);
    return res.text();
  },
  adminSummary: (token: string) => request<AdminSummary>("GET", "/admin/summary", undefined, admin(token)),
  /** The full anonymised export, kept as parsed JSON so it can be saved as a file. */
  adminExport: (token: string) => request<unknown>("GET", "/admin/export", undefined, admin(token)),
};
